import { useState, useRef, useEffect } from 'react'
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Avatar,
  Chip,
  IconButton,
  Card,
  CardContent,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme
} from '@mui/material'
import {
  Send as SendIcon,
  Chat as ChatIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as SpeakIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  SmartToy as AIIcon,
  Speed as StreamIcon
} from '@mui/icons-material'
import { motion, AnimatePresence } from 'framer-motion'
import { apiClient, API_ENDPOINTS, buildApiUrl } from '../config/api'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'

const conversationTopics = [
  { title: 'Giới thiệu bản thân', prompt: 'Hi! Can you tell me about yourself?' },
  { title: 'Sở thích', prompt: 'What are your hobbies and interests?' },
  { title: 'Công việc', prompt: 'What do you do for work? Do you like your job?' },
  { title: 'Du lịch', prompt: 'Have you traveled anywhere interesting recently?' },
  { title: 'Thức ăn', prompt: 'What\'s your favorite food? Can you cook?' },
  { title: 'Phim ảnh', prompt: 'What kind of movies do you like to watch?' },
  { title: 'Học tập', prompt: 'What are you studying or learning these days?' },
  { title: 'Tương lai', prompt: 'What are your plans for the future?' }
]

function Conversation() {
  const [messages, setMessages] = useState([
    {
      role: 'ai',
      content: 'Hello! I\'m Ligo Edu. Let\'s practice speaking English together! What would you like to talk about today?',
      timestamp: new Date().toISOString()
    }
  ])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [studentLevel, setStudentLevel] = useState('B1')
  const [isListening, setIsListening] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [streamingEnabled, setStreamingEnabled] = useState(true)
  const [isStreaming, setIsStreaming] = useState(false)
  const eventSourceRef = useRef(null)
  const chatContainerRef = useRef(null)
  const messagesEndRef = useRef(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) {
      toast.error('Vui lòng nhập tin nhắn')
      return
    }

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    }

    setMessages(prev => [...prev, userMessage])
    const currentMessage = inputMessage
    setInputMessage('')
    
    if (streamingEnabled) {
      await handleStreamingConversation(currentMessage)
    } else {
      await handleRegularConversation(currentMessage)
    }
  }

  const handleRegularConversation = async (message) => {
    setIsLoading(true)
    
    try {
      // Prepare conversation history for API
      const conversationHistory = messages.map(msg => ({
        role: msg.role === 'ai' ? 'assistant' : 'user',
        content: msg.content
      }))

      const response = await apiClient.post(API_ENDPOINTS.TEACHER.CONVERSATION, {
        message,
        conversationHistory,
        studentLevel
      })
      
      const aiMessage = {
        role: 'ai',
        content: response.data.response,
        timestamp: new Date().toISOString()
      }
      
      setMessages(prev => [...prev, aiMessage])
    } catch (error) {
      console.error('Conversation error:', error)
      toast.error('Có lỗi xảy ra khi gửi tin nhắn')
      
      const errorMessage = {
        role: 'ai',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleStreamingConversation = async (message) => {
    setIsStreaming(true)
    
    // Create placeholder message for streaming
    const streamingMessageId = Date.now().toString()
    const placeholderMessage = {
      id: streamingMessageId,
      role: 'ai',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    }
    
    setMessages(prev => [...prev, placeholderMessage])
    
    try {
      // Prepare conversation history for API
      const conversationHistory = messages.map(msg => ({
        role: msg.role === 'ai' ? 'assistant' : 'user',
        content: msg.content
      }))

      // Create EventSource for Server-Sent Events
      const url = buildApiUrl(API_ENDPOINTS.TEACHER.CONVERSATION_STREAM)
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          conversationHistory,
          studentLevel
        })
      })

      if (!response.ok) {
        throw new Error('Failed to start streaming')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      
      while (true) {
        const { done, value } = await reader.read()
        
        if (done) break
        
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              
              if (data.type === 'chunk') {
                // Update the streaming message with new content
                setMessages(prev => prev.map(msg => 
                  msg.id === streamingMessageId 
                    ? { ...msg, content: data.fullContent }
                    : msg
                ))
              } else if (data.type === 'complete') {
                // Mark streaming as complete
                setMessages(prev => prev.map(msg => 
                  msg.id === streamingMessageId 
                    ? { ...msg, content: data.fullContent, isStreaming: false }
                    : msg
                ))
                break
              } else if (data.type === 'error') {
                throw new Error(data.error || 'Streaming error')
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', parseError)
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Streaming conversation error:', error)
      toast.error('Có lỗi xảy ra khi streaming tin nhắn')
      
      // Replace streaming message with error message
      setMessages(prev => prev.map(msg => 
        msg.id === streamingMessageId 
          ? { 
              ...msg, 
              content: 'Sorry, I encountered an error during streaming. Please try again.',
              isStreaming: false 
            }
          : msg
      ))
    } finally {
      setIsStreaming(false)
    }
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  const handleTopicClick = (topic) => {
    setInputMessage(topic.prompt)
  }

  const handleNewConversation = () => {
    setMessages([
      {
        role: 'ai',
        content: 'Hello! I\'m ready for a new conversation. What would you like to talk about?',
        timestamp: new Date().toISOString()
      }
    ])
    toast.success('Đã bắt đầu cuộc hội thoại mới!')
  }

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = 'en-US'
      utterance.rate = 0.8
      speechSynthesis.speak(utterance)
    } else {
      toast.error('Trình duyệt không hỗ trợ text-to-speech')
    }
  }

  const startListening = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      const recognition = new SpeechRecognition()
      
      recognition.lang = 'en-US'
      recognition.continuous = false
      recognition.interimResults = false
      
      recognition.onstart = () => {
        setIsListening(true)
        toast.success('Đang nghe... Hãy nói bằng tiếng Anh')
      }
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript
        setInputMessage(transcript)
        toast.success('Đã nhận diện giọng nói!')
      }
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error)
        toast.error('Lỗi nhận diện giọng nói')
      }
      
      recognition.onend = () => {
        setIsListening(false)
      }
      
      recognition.start()
    } else {
      toast.error('Trình duyệt không hỗ trợ nhận diện giọng nói')
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <ChatIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Luyện hội thoại
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Trò chuyện với Ligo để cải thiện kỹ năng giao tiếp tiếng Anh của bạn
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Chat Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ 
              height: { xs: '75vh', sm: '70vh', md: '70vh' }, 
              display: 'flex', 
              flexDirection: 'column',
              maxHeight: { xs: 'calc(100vh - 200px)', sm: '70vh' }
            }}>
              {/* Chat Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ mr: 2, background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)' }}>
                    <AIIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">Ligo English Teacher</Typography>
                    <Chip label={`Level: ${studentLevel}`} size="small" color="primary" />
                  </Box>
                </Box>
                <Box>
                  <IconButton onClick={() => setSettingsOpen(true)} color="primary">
                    <SettingsIcon />
                  </IconButton>
                  <IconButton onClick={handleNewConversation} color="primary">
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Messages */}
              <Box
                ref={chatContainerRef}
                sx={{
                  flexGrow: 1,
                  p: 2,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <AnimatePresence>
                  {messages.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        display: 'flex',
                        justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '80%',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          flexDirection: message.role === 'user' ? 'row-reverse' : 'row'
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            background: message.role === 'user' 
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)'
                          }}
                        >
                          {message.role === 'user' ? <PersonIcon /> : <AIIcon />}
                        </Avatar>
                        
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            backgroundColor: message.role === 'user' ? 'primary.main' : 'background.paper',
                            color: message.role === 'user' ? 'white' : 'text.primary',
                            borderRadius: 2,
                            position: 'relative'
                          }}
                        >
                          <Box sx={{ '& p': { mb: 1, '&:last-child': { mb: 0 } } }}>
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                            {message.isStreaming && (
                              <Box 
                                component="span" 
                                sx={{ 
                                  display: 'inline-block',
                                  width: '8px',
                                  height: '16px',
                                  backgroundColor: 'primary.main',
                                  ml: 0.5,
                                  animation: 'blink 1s infinite',
                                  '@keyframes blink': {
                                    '0%, 50%': { opacity: 1 },
                                    '51%, 100%': { opacity: 0 }
                                  }
                                }}
                              />
                            )}
                          </Box>
                          
                          {message.role === 'ai' && (
                            <IconButton
                              size="small"
                              onClick={() => speakText(message.content)}
                              sx={{ mt: 1, color: 'primary.main' }}
                            >
                              <SpeakIcon fontSize="small" />
                            </IconButton>
                          )}
                          
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 1,
                              opacity: 0.7,
                              fontSize: '0.7rem'
                            }}
                          >
                            {new Date(message.timestamp).toLocaleTimeString('vi-VN')}
                          </Typography>
                        </Paper>
                      </Box>
                    </motion.div>
                  ))}
                </AnimatePresence>
                
                {(isLoading || isStreaming) && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ display: 'flex', justifyContent: 'flex-start' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)' }}>
                        <AIIcon />
                      </Avatar>
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isStreaming && <StreamIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                          <Typography variant="body2" color="text.secondary">
                            {isStreaming ? 'Ligo đang trả lời...' : 'Ligo đang suy nghĩ...'}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  </motion.div>
                )}
                
                <div ref={messagesEndRef} />
              </Box>

              {/* Input */}
              <Box sx={{ p: { xs: 1, sm: 2 }, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    placeholder="Type your message in English..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading || isStreaming}
                    size={isMobile ? 'small' : 'medium'}
                  />
                  
                  <IconButton
                    color={isListening ? 'error' : 'primary'}
                    onClick={startListening}
                    disabled={isLoading}
                  >
                    {isListening ? <MicOffIcon /> : <MicIcon />}
                  </IconButton>
                  
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={isLoading || isStreaming || !inputMessage.trim()}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    <SendIcon />
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4} sx={{ order: { xs: -1, md: 0 } }}>
            {/* Conversation Topics */}
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                💬 Chủ đề gợi ý
              </Typography>
              
              {conversationTopics.map((topic, index) => (
                <Card
                  key={index}
                  variant="outlined"
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handleTopicClick(topic)}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {topic.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      "{topic.prompt}"
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Paper>

            {/* Tips */}
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                💡 Mẹo luyện hội thoại
              </Typography>
              
              <Box sx={{ '& > div': { mb: 2 } }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>🎯 Tập trung vào giao tiếp</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Đừng lo lắng về lỗi nhỏ, hãy tập trung vào việc truyền đạt ý tưởng
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>🗣️ Sử dụng giọng nói</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nhấn vào icon microphone để luyện phát âm
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>📚 Học từ AI</Typography>
                  <Typography variant="body2" color="text.secondary">
                    AI Ligo sẽ nhẹ nhàng sửa lỗi và gợi ý cách diễn đạt tốt hơn
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>🔄 Luyện tập thường xuyên</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hãy trò chuyện ít nhất 10-15 phút mỗi ngày
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </motion.div>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <DialogTitle>Cài đặt hội thoại</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Trình độ tiếng Anh:</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((level) => (
                <Chip
                  key={level}
                  label={level}
                  onClick={() => setStudentLevel(level)}
                  color={studentLevel === level ? 'primary' : 'default'}
                  variant={studentLevel === level ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
            
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Chế độ trả lời:</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={streamingEnabled}
                  onChange={(e) => setStreamingEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StreamIcon sx={{ fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2">Streaming Mode</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {streamingEnabled 
                        ? 'Nhận phản hồi theo thời gian thực (nhanh hơn)' 
                        : 'Nhận phản hồi đầy đủ một lần (ổn định hơn)'}
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Đóng</Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default Conversation